import React, { useState, useEffect, useRef } from 'react';
import { Button, Select, MenuItem, FormControl, Typography, Box, IconButton, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight, ViewList } from '@mui/icons-material';
import './CompactPaginationControls.css';

export const CompactPaginationControls = ({ 
  pagination, 
  onPageChange, 
  onLimitChange,
  isLoading = false 
}) => {
  const { page, totalPages, hasNext, hasPrev, total, limit } = pagination;
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const handlePrevious = () => {
    if (hasPrev && !isLoading) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (hasNext && !isLoading) {
      onPageChange(page + 1);
    }
  };

  const handleLimitChange = (event) => {
    if (!isLoading) {
      onLimitChange(parseInt(event.target.value));
    }
  };

  const handleIconClick = () => {
    setIsVisible(!isVisible);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside our container
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Don't close if clicking on Material-UI Select dropdown elements
        const isSelectDropdown = event.target.closest('.MuiPopover-root') || 
                                event.target.closest('.MuiMenu-root') ||
                                event.target.closest('.MuiPaper-root') ||
                                event.target.closest('[role="listbox"]') ||
                                event.target.closest('[role="option"]');
        
        if (!isSelectDropdown) {
          setIsVisible(false);
        }
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  // Only show if there are results
  if (!total || total === 0) {
    return null;
  }

  return (
    <div className="pagination-container" ref={containerRef}>
      {/* Pagination Icon */}
      <Tooltip title="Pagination Controls" arrow>
        <IconButton
          onClick={handleIconClick}
          className="pagination-icon"
          size="small"
        >
          <ViewList />
        </IconButton>
      </Tooltip>

      {/* Pagination Controls - Only visible on hover/click */}
      {isVisible && (
        <Box className="compact-pagination-controls">
          {/* Results Info */}
          <div className="pagination-info">
            <Typography variant="caption" className="pagination-text">
              {total.toLocaleString()} results
            </Typography>
          </div>
          
          {/* Navigation Bar */}
          {totalPages > 1 && (
            <div className="navigation-bar">
              <Button
                variant="text"
                size="small"
                startIcon={<ChevronLeft />}
                onClick={handlePrevious}
                disabled={!hasPrev || isLoading}
                className="nav-button prev-button"
              >
                Previous
              </Button>
              
              <div className="page-indicator">
                <Typography variant="caption" className="page-info">
                  {page} of {totalPages}
                </Typography>
              </div>
              
              <Button
                variant="text"
                size="small"
                endIcon={<ChevronRight />}
                onClick={handleNext}
                disabled={!hasNext || isLoading}
                className="nav-button next-button"
              >
                Next
              </Button>
            </div>
          )}

          {/* Page Size Control */}
          <div className="page-size-control">
            <FormControl size="small" className="page-size-form">
              <Select
                value={limit}
                onChange={handleLimitChange}
                disabled={isLoading}
                className="page-size-select"
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#22262e',
                      borderRadius: '0.75rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '& .MuiMenuItem-root': {
                        color: '#eff1f5',
                        fontSize: '0.75rem',
                        minHeight: '44px',
                        padding: '12px 16px',
                        '@media (min-width: 769px)': {
                          minHeight: 'auto',
                          padding: '6px 16px',
                        },
                        '&:hover': {
                          backgroundColor: '#363844',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#363844',
                          '&:hover': {
                            backgroundColor: '#363844',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
              <Typography variant="caption" className="page-size-label">per page</Typography>
            </FormControl>
          </div>
        </Box>
      )}
    </div>
  );
};
